import { Box, styled, useMediaQuery, useTheme } from '@mui/material';
import React, { ReactNode } from 'react';
import { NavigationBar, NavigationItem } from '../navigation/navigationBar';

export function DefaultLayout(props: {
  readonly topBar?: ReactNode;
  readonly footer?: ReactNode;
  readonly children: ReactNode;
  readonly navigation: NavigationItem[];
  readonly activeSection: string | null;
  readonly setActiveSection: (section: string | null) => void;
  readonly isNavBarExpanded: boolean;
  readonly setIsNavBarExpanded: (value: boolean) => void;
}) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Main>
      {props.topBar}

      <LayoutRow>
        <NavArea>
          <NavigationBar
            items={props.navigation}
            activeSection={props.activeSection}
            setActiveSection={props.setActiveSection}
            isNavBarExpanded={props.isNavBarExpanded}
            setIsNavBarExpanded={props.setIsNavBarExpanded}
          />
        </NavArea>

        <ContentWrapper>
          <ContentArea isSmallScreen={isSmallScreen}>{props.children}</ContentArea>
          {props.footer}
        </ContentWrapper>
      </LayoutRow>
    </Main>
  );
}

const Main = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default
}));

const LayoutRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  minHeight: '100vh',
  width: '100%',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column'
  }
}));

const NavArea = styled(Box)(() => ({
  flexShrink: 0,
  height: '100%',
  overflowY: 'auto'
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  marginTop: '80px',
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(6)
  }
}));

const ContentArea = styled(Box, {
  shouldForwardProp: prop => prop !== 'isSmallScreen'
})<{ isSmallScreen: boolean }>(({ theme, isSmallScreen }) => ({
  width: '100%',
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',

  transition: theme.transitions.create(['padding'], {
    duration: theme.transitions.duration.shortest
  }),

  // Symmetric bottom padding (was 0) so content — tables especially — has
  // breathing room above the viewport edge instead of sitting flush.
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(7, 9, 7, 9)
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(7, 6, 7, 6)
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(5, 4, 5, 4)
  },
  [theme.breakpoints.up('xl')]: {
    padding: theme.spacing(8, 10, 8, 10)
  }
}));
